import axios from "axios";
import config from "../../config";
import { enrichApiError } from "../../utilities/apiErrorMessages";

const getToken = () => {
    if (typeof window === "undefined") {
        return null;
    }

    const rawToken = localStorage.getItem("jwt");

    if (!rawToken) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawToken);

        if (typeof parsed === "string") {
            return parsed;
        }

        if (parsed && typeof parsed === "object") {
            return parsed.token ?? null;
        }

        return null;
    } catch (error) {
        // Stored value is the raw token string (non-JSON), return as-is.
        return rawToken;
    }
};

/**
 * Resolves the current language from localStorage or browser settings
 * Always returns a valid locale ('en' or 'ar')
 */
export const resolveLanguage = () => {
    if (typeof window === "undefined") {
        return "en";
    }

    const storedLanguage = localStorage.getItem("appLanguage");
    if (storedLanguage && (storedLanguage === "en" || storedLanguage === "ar")) {
        return storedLanguage;
    }

    if (typeof navigator !== "undefined") {
        const browserLang = (navigator.language || navigator.userLanguage || "en").toLowerCase();
        const langCode = browserLang.split(/[-_]/)[0];
        if (langCode === "ar") {
            return "ar";
        }
    }

    return "en";
};

/**
 * Gets the normalized locale code (always 'en' or 'ar')
 */
export const getLocale = () => {
    const language = resolveLanguage();
    const normalizedLanguage = (language || "en").toLowerCase();
    return normalizedLanguage.split(/[-_]/)[0] || "en";
};

/**
 * Returns headers object with X-Language and Accept-Language headers
 * Use this helper for direct fetch/axios calls that don't use the http instance
 */
export const getLanguageHeaders = () => {
    const locale = getLocale();
    return {
        "X-Language": locale,
        "Accept-Language": locale,
    };
};

const http = axios.create({
    baseURL: `${config.apiUrl}`,
    headers: {
        "Content-type": "application/json",
        Accept: "application/json",
    },
});

http.interceptors.request.use((requestConfig) => {
    requestConfig.headers = requestConfig.headers ?? {};

    const token = getToken();
    const locale = getLocale();

    // Always set language headers - this ensures all requests include locale
    requestConfig.headers["X-Language"] = locale;
    requestConfig.headers["Accept-Language"] = locale;

    if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
    } else {
        delete requestConfig.headers.Authorization;
    }

    // Debugging aid: log the outgoing locale header
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[http-common] X-Language ->', locale, 'for', requestConfig.url);
    }

    return requestConfig;
});

http.interceptors.response.use(
    (response) => response,
    (error) => Promise.reject(enrichApiError(error)),
);

export default http;